import React, { useEffect, useMemo, useRef } from 'react';
import {
  Apartment,
  Check,
  DoneAll,
  Error as ErrorIcon
} from '@mui/icons-material';
import {
  alpha,
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  styled,
  Tooltip,
  Typography
} from '@mui/material';

import {
  getAccessibleFacilityTypes,
  getCareSettingTerminologyForFacilityType,
  getCareSettingTerminologyForFacilityTypes
} from '@/hooks/facility-selection/careSettingTerminology';
import { isFacilityTypeAllowed } from '@/hooks/facility-selection/screenerFacilityTypeHelpers';
import { ALL_FACILITIES, FacilityPayload } from '@/hooks/useFacilitiesQuery';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import { useKeyboardNavigation } from '@/hooks/useKeyboardNavigation';
import { UserModel } from '@/models/UserModel';
import useIsScreenerAllFacilitiesSelected from '@/pages/CRM/Screener/Leads/AllLeads/hooks/useIsScreenerAllFacilitiesSelected';
import { FacilityFacilityTypeEnum } from '@/types/crm-screener';
import { UserFacilityAccess } from '@/types/users';

import CrmScreenerSelectAllButton from './CrmScreenerSelectAllButton';
import FacilitySearch from './FacilitySearch';

// Two-line option: 8px padding + 20px name + 2px gap + 16px address + 8px padding
const FACILITY_ROW_HEIGHT = 54;
const VISIBLE_ROWS = 6;

export const FacilitySelectorListItemButton = styled(ListItemButton)(
  ({ theme }) => ({
    minHeight: 48,
    padding: '8px 16px',
    gap: 8,
    '&:hover, &.Mui-selected, &.Mui-selected:hover, &.Mui-focusVisible': {
      backgroundColor: theme.palette.info.light
    },
    '& .MuiListItemText-root': {
      margin: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 2
    },
    '& .MuiListItemText-primary': {
      fontSize: '14px',
      fontWeight: 400,
      color: theme.palette.text.primary,
      lineHeight: '20px'
    },
    '& .MuiListItemText-secondary': {
      display: 'flex',
      alignItems: 'center',
      fontSize: '12px',
      fontWeight: 400,
      color: theme.palette.neutral.caption,
      lineHeight: '16px'
    },
    '& .MuiSvgIcon-root': {
      fontSize: 20,
      color: theme.palette.neutral.caption
    },
    '& .MuiListItemIcon-root': {
      minWidth: 'unset'
    }
  })
);

interface FacilitySelectorPopoverProps {
  selectedFacilityId: string | null;
  anchorEl: HTMLDivElement;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  user: UserModel;
  onSelectFacility: (facilityId: string) => void;
  canSelectAllFacilities: boolean;
  isCRM: boolean;
  billingDisabledFacilityIds: Set<string>;
  anyFacilityBillingDisabled: boolean;
  facilities: FacilityPayload[];
  accessibleFacilityIds: ReadonlySet<string>;
  allowedFacilityTypes?: FacilityFacilityTypeEnum[];
}

const FacilitySelectorPopover: React.FC<FacilitySelectorPopoverProps> = ({
  selectedFacilityId,
  onSelectFacility,
  isOpen,
  user,
  setIsOpen,
  anchorEl,
  canSelectAllFacilities,
  isCRM,
  billingDisabledFacilityIds,
  anyFacilityBillingDisabled,
  facilities,
  accessibleFacilityIds,
  allowedFacilityTypes
}) => {
  const isMobileViewport = useIsMobileViewport();
  const canSelectUntypedAllFacilities =
    !isCRM && canSelectAllFacilities && !isMobileViewport;
  const [inputValue, setInputValue] = React.useState('');
  const [filteredFacilities, setFilteredFacilities] = React.useState<
    UserFacilityAccess[]
  >(user.user_facility_accesses ?? []);
  const isAllFacilitiesSelected = useIsScreenerAllFacilitiesSelected();
  const rawOrganizationName = user.organization_details?.name;
  const organizationName =
    typeof rawOrganizationName === 'string' ? rawOrganizationName.trim() : '';
  const organizationNameId = React.useId();
  const shouldShowSearch = (user.user_facility_accesses?.length ?? 0) >= 5;
  const careSettingTerms = useMemo(
    () =>
      getCareSettingTerminologyForFacilityTypes(
        getAccessibleFacilityTypes(facilities, accessibleFacilityIds)
      ),
    [accessibleFacilityIds, facilities]
  );
  const facilityTypeById = useMemo(
    () =>
      new Map(
        facilities.map((facility) => [facility.id, facility.facility_type])
      ),
    [facilities]
  );

  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const { handleSearchKeyDown, handleListKeyDown } = useKeyboardNavigation({
    searchInputRef,
    listRef
  });

  useEffect(() => {
    const filteredFacilities =
      user.user_facility_accesses?.filter((facility) => {
        const matchesSearch = facility.facility?.name
          ?.toLowerCase()
          .includes((inputValue ?? '').toLowerCase());
        const matchesType = isFacilityTypeAllowed(
          facilityTypeById.get(facility.facility_id),
          allowedFacilityTypes
        );
        return matchesSearch && matchesType;
      }) ?? [];
    setFilteredFacilities(
      filteredFacilities.sort((a, b) => {
        return a.facility.name.localeCompare(b.facility.name);
      })
    );
  }, [
    inputValue,
    user.user_facility_accesses,
    allowedFacilityTypes,
    facilityTypeById
  ]);

  return (
    <Popover
      PaperProps={{
        sx: {
          width: isMobileViewport ? '100%' : 280,
          mt: 0.5,
          pt: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: (theme) =>
            `0px 5px 30px 0px ${alpha(theme.palette.neutral.shadow, 0.3)}`,
          borderRadius: '8px'
        }
      }}
      anchorEl={anchorEl}
      open={isOpen}
      onClose={() => setIsOpen(false)}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left'
      }}
    >
      {organizationName && (
        <Box sx={{ px: 2, pt: 1, pb: shouldShowSearch ? 0 : 1 }}>
          <Typography
            id={organizationNameId}
            variant="body2"
            color="text.secondary"
            lineHeight="20px"
            noWrap
            title={organizationName}
          >
            {organizationName}
          </Typography>
        </Box>
      )}
      {shouldShowSearch && (
        <>
          <FacilitySearch
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
            autoFocus={true}
            onKeyDown={handleSearchKeyDown}
            inputRef={searchInputRef}
            placeholder={`Search ${careSettingTerms.pluralTitle}`}
          />
          <Divider sx={{ mx: 1, my: 0.5, borderColor: 'info.main' }} />
        </>
      )}
      <List
        dense
        sx={{
          p: 0,
          flex: 1,
          minHeight: 0,
          maxHeight: VISIBLE_ROWS * FACILITY_ROW_HEIGHT,
          overflowY: 'auto'
        }}
        role="listbox"
        aria-describedby={organizationName ? organizationNameId : undefined}
        ref={listRef}
        onKeyDown={handleListKeyDown}
      >
        <CrmScreenerSelectAllButton
          setIsOpen={setIsOpen}
          hide={inputValue.trim().length > 0}
          billingDisabledFacilityIds={billingDisabledFacilityIds}
          facilities={facilities}
          accessibleFacilityIds={accessibleFacilityIds}
        />
        {canSelectUntypedAllFacilities && (
          <FacilitySelectorListItemButton
            role="option"
            selected={selectedFacilityId === ALL_FACILITIES}
            disabled={anyFacilityBillingDisabled}
            onClick={() => {
              onSelectFacility(ALL_FACILITIES);
              setIsOpen(false);
            }}
            sx={anyFacilityBillingDisabled ? { opacity: 0.5 } : undefined}
          >
            <ListItemIcon>
              <DoneAll />
            </ListItemIcon>
            <ListItemText>Select All</ListItemText>
            {anyFacilityBillingDisabled && (
              <Tooltip
                title={`All ${careSettingTerms.pluralTitle} view is disabled due to an unpaid balance on one or more ${careSettingTerms.pluralLower}.`}
                placement="top"
              >
                <ErrorIcon
                  sx={{
                    fontSize: 18,
                    color: '#D32F2F !important',
                    ml: 1,
                    pointerEvents: 'auto'
                  }}
                />
              </Tooltip>
            )}
          </FacilitySelectorListItemButton>
        )}
        {filteredFacilities.length > 0 ? (
          filteredFacilities.map((item) => {
            const isBillingDisabled = billingDisabledFacilityIds.has(
              item.facility_id
            );
            const itemCareSettingTerms =
              getCareSettingTerminologyForFacilityType(
                facilityTypeById.get(item.facility_id)
              );
            const isSelected =
              !isAllFacilitiesSelected &&
              item.facility_id === selectedFacilityId;
            return (
              <FacilitySelectorListItemButton
                key={item.id}
                role="option"
                id={`community-${item.facility_id}`}
                onClick={() => onSelectFacility(item.facility_id)}
                selected={isSelected}
              >
                <ListItemIcon>
                  <Apartment />
                </ListItemIcon>
                <ListItemText secondary={<>{item.facility.address}</>}>
                  {item.facility.name}
                </ListItemText>
                {isBillingDisabled && (
                  <Tooltip
                    title={`This ${itemCareSettingTerms.singularLower} has been temporarily disabled due to an outstanding balance on your account.`}
                    placement="top"
                  >
                    <ErrorIcon
                      sx={{ fontSize: 18, color: '#D32F2F !important', ml: 1 }}
                    />
                  </Tooltip>
                )}
                {isSelected && <Check sx={{ ml: 'auto' }} />}
              </FacilitySelectorListItemButton>
            );
          })
        ) : (
          <Typography
            sx={{
              px: 2,
              py: 1.5,
              fontSize: '14px',
              lineHeight: '20px',
              color: 'neutral.caption'
            }}
          >
            {inputValue.trim()
              ? `No results found for “${inputValue.trim()}”`
              : `No ${careSettingTerms.pluralTitle} Found`}
          </Typography>
        )}
      </List>
    </Popover>
  );
};

export default FacilitySelectorPopover;
